import React, { useEffect, useRef, useState } from "react";
import FaqAssistMark from "../FAQ/FaqAssistMark";
import { formatFormAssistSessionMeta } from "./esourceFormAssistChatStore";
import {
  ASSIST_DOC_ACCEPT,
  validateAssistDocumentFile,
} from "./esourceAssistDocumentImport";

const FORM_ASSIST_DISCLAIMER =
  "AQ Assist is AI-generated and may be inaccurate or incomplete. Review field labels and types before saving.";

/**
 * FAQ Assist–style thread + composer for Form Builder AQ Assist.
 */
export default function ESourceFormAssistPane({
  mode = "create",
  draft,
  onDraftChange,
  messages = [],
  busy = false,
  hasCanvasFields = false,
  recentSessions = [],
  activeSessionId = "",
  usage = { used: 0, cap: 50 },
  onSend,
  onNewChat,
  onOpenSession,
}) {
  const threadRef = useRef(null);
  const fileInputRef = useRef(null);
  const [floatPanel, setFloatPanel] = useState(null); // null | "recent" | "usage"
  const [attachedFile, setAttachedFile] = useState(null);
  const [attachError, setAttachError] = useState("");

  useEffect(() => {
    const el = threadRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [messages, busy]);

  useEffect(() => {
    if (!floatPanel) return undefined;
    const onKey = (event) => {
      if (event.key === "Escape") setFloatPanel(null);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [floatPanel]);

  const canSend =
    !busy &&
    (Boolean(String(draft || "").trim()) || Boolean(attachedFile));
  const used = Math.max(0, Number(usage?.used) || 0);
  const cap = Math.max(1, Number(usage?.cap) || 50);
  const remaining = Math.max(0, cap - used);
  const usagePct = Math.min(100, Math.round((used / cap) * 100));
  const closeFloat = () => setFloatPanel(null);

  const openFilePicker = () => {
    if (busy) return;
    fileInputRef.current?.click();
  };

  const clearAttachedFile = () => {
    setAttachedFile(null);
    setAttachError("");
  };

  const onFilePicked = (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    try {
      validateAssistDocumentFile(file);
      setAttachedFile(file);
      setAttachError("");
    } catch (err) {
      setAttachedFile(null);
      setAttachError(
        err?.message ||
          "Only PDF and Word files (.pdf, .doc, .docx) are allowed.",
      );
    }
  };

  const handleSend = () => {
    if (!canSend) return;
    const payload = {
      text: String(draft || "").trim(),
      file: attachedFile || null,
    };
    setAttachedFile(null);
    setAttachError("");
    onSend?.(payload);
  };

  return (
    <div className="esource-builder-assist-chat">
      <div
        className="esource-builder-assist-chat__thread"
        aria-live="polite"
        ref={threadRef}
      >
        {messages.length === 0 && !busy ? (
          <div className="esource-builder-assist-chat__empty">
            <FaqAssistMark bordered />
            <p className="esource-builder-assist-chat__empty-title">
              Ask AQ Assist to build or edit this form
            </p>
            <p className="esource-builder-assist-chat__empty-sub">
              {hasCanvasFields
                ? "Write in everyday English — or attach a PDF / Word file (+) then press Send. Watch for “Canvas updated” under the reply."
                : "Describe the form, or attach a PDF / Word file (+) then press Send to generate fields."}
            </p>
          </div>
        ) : (
          messages.map((msg) => (
            <div
              key={msg.id}
              className={`esource-builder-assist-chat__bubble esource-builder-assist-chat__bubble--${msg.role}${
                msg.error || msg.blocked ? " is-error" : ""
              }`}
            >
              {msg.role === "assistant" ? (
                <>
                  <p className="esource-builder-assist-chat__text">{msg.text}</p>
                  {msg.applied ? (
                    <div className="esource-builder-assist-chat__fields esource-builder-assist-chat__fields--applied">
                      <span className="esource-builder-assist-chat__fields-label">
                        Canvas updated
                      </span>
                      {Array.isArray(msg.fieldLabels) && msg.fieldLabels.length ? (
                        <ul>
                          {msg.fieldLabels.map((label, idx) => (
                            <li key={`${label}-${idx}`}>{label}</li>
                          ))}
                        </ul>
                      ) : null}
                    </div>
                  ) : null}
                  {!msg.applied &&
                  !msg.error &&
                  !msg.blocked &&
                  msg.role === "assistant" ? (
                    <div className="esource-builder-assist-chat__fields esource-builder-assist-chat__fields--advice">
                      <span className="esource-builder-assist-chat__fields-label">
                        Advice only — canvas unchanged
                      </span>
                    </div>
                  ) : null}
                  {msg.error && !msg.blocked ? (
                    <div className="esource-builder-assist-chat__fields esource-builder-assist-chat__fields--failed">
                      <span className="esource-builder-assist-chat__fields-label">
                        Not applied to canvas
                      </span>
                    </div>
                  ) : null}
                  {!msg.error && !msg.blocked ? (
                    <p className="esource-builder-assist-chat__disclaimer">
                      {FORM_ASSIST_DISCLAIMER}
                    </p>
                  ) : null}
                </>
              ) : (
                <p className="esource-builder-assist-chat__text">{msg.text}</p>
              )}
            </div>
          ))
        )}

        {busy ? (
          <div
            className="esource-builder-assist-chat__typing"
            role="status"
            aria-live="polite"
            aria-label="Generating response"
          >
            <span
              className="esource-builder-assist-chat__typing-avatar"
              aria-hidden="true"
            >
              <FaqAssistMark />
            </span>
            <div
              className="esource-builder-assist-chat__typing-dots"
              aria-hidden="true"
            >
              <span />
              <span />
              <span />
            </div>
            <span className="esource-builder-assist-chat__typing-label">
              Generating…
            </span>
          </div>
        ) : null}
      </div>

      <div className="esource-builder-assist-chat__composer">
        <div className="esource-builder-assist-chat__composer-shell">
          {attachedFile ? (
            <div className="esource-builder-assist-chat__file-chip">
              <i className="fas fa-paperclip" aria-hidden="true" />
              <span title={attachedFile.name}>{attachedFile.name}</span>
              <button
                type="button"
                className="esource-builder-assist-chat__file-chip-remove"
                onClick={clearAttachedFile}
                disabled={busy}
                aria-label="Remove attached file"
                title="Remove file"
              >
                <i className="fas fa-xmark" aria-hidden="true" />
              </button>
            </div>
          ) : null}
          {attachError ? (
            <p className="esource-builder-assist-chat__attach-error" role="alert">
              {attachError}
            </p>
          ) : null}
          <textarea
            className="esource-builder-assist-chat__input"
            rows={2}
            value={draft}
            onChange={(e) => onDraftChange?.(e.target.value)}
            placeholder={
              attachedFile
                ? "Optional note, then press Send…"
                : mode === "edit"
                  ? "How should I change this form? Or attach PDF / Word (+)"
                  : "e.g. Vital signs form — or attach a PDF / Word file (+)"
            }
            disabled={busy}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                if (canSend) handleSend();
              }
            }}
          />
          <div className="esource-builder-assist-chat__composer-footer">
            <input
              ref={fileInputRef}
              type="file"
              className="esource-builder-assist-chat__file-input"
              accept={ASSIST_DOC_ACCEPT}
              onChange={onFilePicked}
              tabIndex={-1}
              aria-hidden="true"
            />
            <button
              type="button"
              className="esource-builder-assist-chat__attach"
              data-tour="esource-fb-assist-attach"
              onClick={openFilePicker}
              disabled={busy}
              aria-label="Attach PDF or Word document"
              title="Attach PDF or Word (.pdf, .doc, .docx) — then press Send"
              data-tour="esource-fb-assist-attach"
              onClick={openFilePicker}
              disabled={busy}
              aria-label="Attach PDF or Word document"
              title="Attach PDF or Word (.pdf, .doc, .docx) — then press Send"
            >
              <i className="fas fa-plus" aria-hidden="true" />
            </button>
            <button
              type="button"
              className={`esource-builder-assist-chat__send${
                busy ? " is-sending" : ""
              }`}
              onClick={handleSend}
              disabled={!canSend}
              aria-label={busy ? "Generating response" : "Send"}
              title={busy ? "Generating…" : "Send"}
            >
              <i
                className={`fas ${busy ? "fa-spinner fa-spin" : "fa-wand-magic-sparkles"}`}
                aria-hidden="true"
              />
              <span>{busy ? "Generating…" : "Send"}</span>
            </button>
          </div>
        </div>
      </div>

      <div className="esource-builder-assist-chat__toolbar">
        <button
          type="button"
          className="esource-builder-assist-chat__tool-btn"
          onClick={() => {
            closeFloat();
            onNewChat?.();
          }}
          disabled={busy}
          title="Start a new chat"
        >
          <i className="fas fa-plus" aria-hidden="true" />
          New chat
        </button>

        <button
          type="button"
          className={`esource-builder-assist-chat__tool-btn${
            floatPanel === "recent" ? " is-active" : ""
          }`}
          onClick={() =>
            setFloatPanel((v) => (v === "recent" ? null : "recent"))
          }
          disabled={busy}
          aria-expanded={floatPanel === "recent"}
          aria-haspopup="dialog"
          title="Recent chats"
        >
          <i className="fas fa-clock-rotate-left" aria-hidden="true" />
          Recent
          {recentSessions.length ? (
            <span className="esource-builder-assist-chat__tool-count">
              {recentSessions.length}
            </span>
          ) : null}
        </button>

        <button
          type="button"
          className={`esource-builder-assist-chat__tool-btn${
            floatPanel === "usage" ? " is-active" : ""
          }`}
          onClick={() =>
            setFloatPanel((v) => (v === "usage" ? null : "usage"))
          }
          aria-expanded={floatPanel === "usage"}
          aria-haspopup="dialog"
          title="Today’s usage"
        >
          <i className="fas fa-gauge-high" aria-hidden="true" />
          Usage
        </button>
      </div>

      {floatPanel ? (
        <div className="esource-builder-assist-chat__modal">
          <button
            type="button"
            className="esource-builder-assist-chat__modal-backdrop"
            aria-label="Close"
            onClick={closeFloat}
          />
          <div
            className="esource-builder-assist-chat__modal-card"
            role="dialog"
            aria-modal="true"
            aria-label={
              floatPanel === "recent" ? "Recent chats" : "Today’s usage"
            }
          >
            <div className="esource-builder-assist-chat__modal-head">
              <span>
                {floatPanel === "recent" ? "Recent chats" : "Today’s usage"}
              </span>
              <button
                type="button"
                className="esource-builder-assist-chat__modal-close"
                aria-label="Close"
                onClick={closeFloat}
              >
                <i className="fas fa-times" aria-hidden="true" />
              </button>
            </div>

            {floatPanel === "recent" ? (
              recentSessions.length === 0 ? (
                <p className="esource-builder-assist-chat__modal-empty">
                  Your recent chats will appear here after you send a prompt.
                </p>
              ) : (
                <ul className="esource-builder-assist-chat__modal-list">
                  {recentSessions.map((session) => {
                    const isActive = session.id === activeSessionId;
                    return (
                      <li key={session.id}>
                        <button
                          type="button"
                          className={`esource-builder-assist-chat__modal-item${
                            isActive ? " is-active" : ""
                          }`}
                          onClick={() => {
                            onOpenSession?.(session);
                            closeFloat();
                          }}
                        >
                          <span className="esource-builder-assist-chat__modal-item-title">
                            {session.title || "Chat"}
                          </span>
                          <span className="esource-builder-assist-chat__modal-item-meta">
                            {formatFormAssistSessionMeta(session)}
                            {isActive ? " · Open" : ""}
                          </span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )
            ) : (
              <div className="esource-builder-assist-chat__usage-card">
                <div className="esource-builder-assist-chat__usage-row">
                  <span className="esource-builder-assist-chat__usage-label">
                    You
                  </span>
                  <span className="esource-builder-assist-chat__usage-value">
                    {used} used · {remaining} left
                  </span>
                </div>
                <div
                  className="esource-builder-assist-chat__usage-bar"
                  aria-hidden="true"
                >
                  <span style={{ width: `${usagePct}%` }} />
                </div>
                <p className="esource-builder-assist-chat__usage-note">
                  Form Builder AQ Assist prompts today (organisation quota).
                  Daily cap: {cap}.
                </p>
              </div>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}
